"use client";

import { useEffect, useRef } from "react";
import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import TextLink from "@/app/components/atoms/TextLink";
import DynamicForm from "@/app/components/organisms/DynamicForm";
import { trackAuthEvent } from "@/lib/analytics/gtag";
import type { FormConfig } from "@/app/components/types";

/**
 * @const passwordConfig
 * @description Dynamic form configuration for the password step of the login flow.
 */
const passwordConfig: FormConfig = {
  id: "password-form",
  fields: [
    {
      name: "email",
      label: "Email",
      type: "text",
      readOnly: true,
    },
    {
      name: "password",
      label: "password",
      type: "password",
      placeholder: "Password",
      required: true,
      autoFocus: true,
    },
  ],
  submitLabel: "Continue",
  loadingLabel: "Signing in\u2026",
  ariaLabelledBy: "password-step-heading",
};

interface PasswordStepProps {
  email: string;
  onBack: () => void;
  onContinue: (password: string, rememberMe: boolean) => void;
  error?: string | null;
  loading?: boolean;
}

/**
 * @component PasswordStep
 * @description Second step of the login flow. Shows a back link, the user's
 * email (read-only), a password input with show/hide toggle, continue button,
 * and a forgot password link.
 * @param email - The email from the previous step.
 * @param onBack - Callback to return to the email step.
 * @param onContinue - Callback when the user clicks Continue.
 * @param error - Optional error message from a failed login attempt.
 * @param loading - Whether a login attempt is in progress.
 * @returns The password entry form.
 */
export default function PasswordStep({
  email,
  onBack,
  onContinue,
  error,
  loading,
}: PasswordStepProps) {
  const prevErrorRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      trackAuthEvent("password_submitted", "error", "wrong_password");
    }
    prevErrorRef.current = error;
  }, [error]);

  return (
    <div className="flex flex-col gap-8 ">
      <div className="w-full max-w-md m-auto flex flex-col gap-8 relative">
        <div className="mb-4 md:mb-0 md:absolute md:top-[-3rem] md:left-0">
          <button
            type="button"
            onClick={() => {
              trackAuthEvent("step_back");
              onBack();
            }}
            aria-label="Go back"
            className="text-body-base text-charcoal hover:underline self-start"
          >
            &larr; Back
          </button>
        </div>
        <StepHeading id="password-step-heading" title="Enter your password" />

        <FormCard>
          <DynamicForm
            config={passwordConfig}
            initialValues={{ email, password: "" }}
            onSubmit={(data) => {
              trackAuthEvent("password_submitted", "success");
              onContinue(String(data["password"] ?? ""), true);
            }}
            loading={loading}
            fieldErrors={
              error
                ? {
                    password:
                      "Wrong password. Try again or click “Forgot Password” for more options.",
                  }
                : undefined
            }
          />
        </FormCard>
        <TextLink
          as="a"
          href="/forgot-password"
          className="text-charcoal self-end"
          onClick={() => trackAuthEvent("forgot_password_clicked")}
        >
          Forgot Password?
        </TextLink>
      </div>
    </div>
  );
}
